/**
 * @module app.um
 */

import { autoinject, observable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ComponentBase, State, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { LookupService as cdLookupService } from 'app/cd/services/lookup-service';
import { LookupService as coreLookupService } from 'app/core/services/lookup-service';
import { AuthPageContext } from '../../lib/auth-page-context';
import { deepClone } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { apiCall } from 'app/shared/lib/api/api';
import { AuthReloadEvent, AuthChangeEvent } from 'app/um/lib/events';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { naturalSort } from 'app/shared/lib/arrays';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';


@autoinject()
export class AuthInfoPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private authId: string | null = null;
    protected auth: UM.Auth | null = null;
    protected editAuth: UM.Auth | null = null;
    protected readonly: boolean = false;

    protected sources: ReadonlyArray<UM.AuthSource> = [];
    protected healthcareServiceTypes: ReadonlyArray<UM.HealthcareServiceType> = [];
    protected placesOfService: ReadonlyArray<CD.PlaceOfService> = [];
    protected priorities: ReadonlyArray<UM.AuthPriority> = [];
    protected admissionSources: ReadonlyArray<UM.AdmissionSource> = [];
    protected resources: ReadonlyArray<Core.Resource> = [];
    protected commonProviders: ReadonlyArray<UM.CommonProvider> = [];
    protected regions: Core.AddressRegion[] = [];
    protected genders: Core.Gender[] = [];
    protected types: CD.AuthProviderType[] = [];
    @observable
    protected memberPlan: CD.MemberPlan | null = null;

    protected validate = validate;

    private infoState: State = new State();
    private authReloaded: Subscription | null = null;

    private resourcesSorting: Sorting = new Sorting();
    protected resourcesResultCount: number = 0;
    protected resourcesPageSize: number = 10;


    ////****************** Element References ***************************************////
    protected $resourcesPager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly cdLookupService: cdLookupService,
        private readonly coreLookupService: coreLookupService,
        private readonly context: AuthPageContext,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);

        this.commonProviderFilter.bind(this);
    }

    protected async canActivate(params: { authId: string }): Promise<boolean> {
        if(!params.authId) { throw new Error('Missing or invalid authId parameter'); }

        return true;
    }

    protected async activate(params: any): Promise<void> {
        if(this.authId !== params.authId) { this.authId = params.authId; }

        this.loadLookups();

        if(this.context && this.context.auth) {
            this.auth = this.context.auth;
            this.editAuth = deepClone(this.auth);
            this.memberPlan = this.context.auth.member?.plan || null;
            this.loadAuthProviders();
            this.loadResources();
            this.checkPermissions();
        } else {
            throw new Error('Context not loaded yet!');
        }

        this.authReloaded = this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => {
            this.auth = this.context.auth;
            this.editAuth = deepClone(this.auth);
            this.checkPermissions();
            this.loadLookups();
            this.loadResources();
            this.loadAuthProviders();
        });
    }

    protected deactivate(): void {
        if(this.authReloaded) this.authReloaded.dispose();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applyResourcesSort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { resourcesSorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadResources();
    }


    ////****************** Functions ************************************************////
    private async reload(detail?: { authReloaded?: boolean, triggerRules?: boolean }): Promise<void> {
        const shouldReloadContext = !detail || detail.authReloaded !== true;

        this.toaster.info().message(this.i18n.tr('um:local.auth.refreshingAuthInformation')).show();
        try {
            this.infoState.beginLoad();

            if(shouldReloadContext) {
                await this.context.refreshAuthSettings();
            }

            this.auth = this.context.auth;
            this.editAuth = deepClone(this.auth);
            this.checkPermissions();
            this.memberPlan = this.context.auth.member?.plan || null;
        } catch(error) {
            throw error;
        } finally {
            this.infoState.endLoad();
        }
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        const canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.readonly = this.context?.authIsReadOnly(canEditAuth, canEditClosedAuth);
    }

    private commonProviderFilter(providerType: UM.AuthProviderType): (v: any) => boolean {
        return (cp) => cp.providerType && cp.providerType.id === providerType.id;
    }

    protected resetInfo(): void {
        if(this.auth) {
            this.editAuth = deepClone(this.auth);
        }
    }

    protected async saveInfo(): Promise<void> {
        if(this.editAuth === null || this.editAuth === undefined) {
            return;
        }

        const saveAuthObject: UM.Auth = {
            id: this.editAuth.id,
            healthcareServiceType: this.editAuth.healthcareServiceType ? { id: this.editAuth.healthcareServiceType.id } : null,
            source: this.editAuth.source ? { id: this.editAuth.source.id } : null,
            placeOfService: this.editAuth.placeOfService ? { id: this.editAuth.placeOfService.id } : null,
            priority: this.editAuth.priority ? { id: this.editAuth.priority.id } : null,
            admission: {
                source: this.editAuth.admission && this.editAuth.admission.source ? { id: this.editAuth.admission.source.id } : null
            },
            nextReviewDate: this.editAuth.nextReviewDate ? this.editAuth.nextReviewDate : null
        }

        this.saveAuth(saveAuthObject);

    }

    protected memberPlanChanged(newValue: CD.MemberPlan): void {
        if(this.auth && this.auth.id && this.auth.member && this.auth.member.plan && newValue && this.auth.member.plan.id !== newValue.id) {
            if(!newValue.id) {
                this.toaster.error().message(this.i18n.tr('cd:local.memberPlanNotSelected')).show();
                return;
            }
            const editAuth: UM.Auth = {
                id: this.auth.id,
                member: {
                    id: newValue.id,
                    plan: { id: newValue.id }
                }
            }

            this.saveAuth(editAuth);
        }
    }

    protected buildOverriddenStamp(requestedOverride: UM.ReasonModelStamp): string {
        const key = 'ui:stamps.overridden.timestamp';
        const timestamp = requestedOverride.timestamp;
        const user = requestedOverride?.byUser?.$display;
        return this.i18n.tr(key, { timestamp, user });
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveAuth(editAuth: UM.Auth): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.saveAuth(editAuth);
            isSuccessful = response.isSuccessful;
            await this.reload();
            this.events.publish(new AuthChangeEvent(true));
            this.events.publish(new AuthReloadEvent());
            this.context.triggerCorrespondenceRules();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.updated('um:auth.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const descCodeSorting = new Sorting();
        descCodeSorting.add({
            reference: 'description',
            direction: SortDirection.Ascending
        });
        descCodeSorting.add({
            reference: 'code',
            direction: SortDirection.Ascending
        });

        const descCodeSortingOptions: Partial<IQueryLoadOptions> = {
            all: true,
            sorting: descCodeSorting
        };

        const descSorting = new Sorting();
        descSorting.add({
            reference: 'description',
            direction: SortDirection.Ascending
        });

        const descSortingOptions: Partial<IQueryLoadOptions> = {
            all: true,
            sorting: descSorting
        };

        try {
            this.state.beginLoad();

            const loadAuthSources = this.context.sourceOfAuthVisible && !this.sources.length;
            const loadAuthPriorities = this.context.settings.priority.visible && !this.priorities.length;
            const loadHealthcareServiceTypes = this.context.settings.healthcareServiceType.visible && !this.healthcareServiceTypes.length;
            const loadPlacesOfService = this.context.settings.placeOfService.visible && !this.placesOfService.length;
            const loadSourcesOfAdmission = this.context.settings.admissionSource.visible && !this.admissionSources.length;
            const loadRegions = !this.regions.length;
            const loadGenders = !this.genders.length;

            const [sources, priorities, healthcareServiceTypes, placesOfService, admissionSources, regions, genders] = await Promise.all([
                loadAuthSources ? this.authService.loadAuthSources(options, undefined, ['description']) : null,
                loadAuthPriorities ? this.authService.loadAuthPriorities(options, undefined, ['description']) : null,
                loadHealthcareServiceTypes ? this.authService.loadHealthcareServiceTypes(descCodeSortingOptions, undefined, ['code', 'description']) : null,
                loadPlacesOfService ? this.cdLookupService.loadPlacesOfService(undefined, descCodeSortingOptions, undefined, ['code', 'description']) : null,
                loadSourcesOfAdmission ? this.authService.loadAdmissionSources(options, undefined, ['description']) : null,
                loadRegions ? this.coreLookupService.loadStates(descSortingOptions, undefined, ['code', 'description']) : null,
                loadGenders ? this.coreLookupService.loadGenders(descSortingOptions, undefined, undefined, true, ['description']) : null
            ]);

            if(sources) this.sources = sources.results;
            if(priorities) this.priorities = priorities.results;
            if(healthcareServiceTypes) this.healthcareServiceTypes = naturalSort(healthcareServiceTypes.results, 'code');
            if(placesOfService) this.placesOfService = naturalSort(placesOfService.results, 'code');
            if(admissionSources) this.admissionSources = admissionSources.results;
            if(regions) this.regions = regions.results;
            if(genders) this.genders = genders.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadResources(): Promise<void> {
        if(!this.auth?.class?.subClass?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.resourcesPageSize,
            page: this.$resourcesPager?.page || 0,
            sorting: this.resourcesSorting
        };

        const fields = [
            'description',
            'href',
            'summary'
        ];

        try {
            this.state.beginLoad();
            const resources = await this.authService.loadAuthSubClassResources(this.auth.class.subClass.id, options, fields);
            this.resources = resources.results;
            this.resourcesResultCount = resources.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAuthProviders(): Promise<void> {
        if(!this.auth?.class?.subClass?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = [
            'providerType',
            'subclass'
        ];

        const fields = [
            'description',
            'providerLocation.id',
            'providerType.key'
        ];

        try {
            this.state.beginLoad();

            const response = await this.authService.loadCommonProvidersBySubClass(this.auth.class.subClass.id, options, includes, fields);
            this.commonProviders = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
